import React from "react";
import { jsPDF } from "jspdf";
import { downloadBlob, shareOrDownloadBlob } from "../../downloadUtils";
import { useLanguage } from "../../i18n";
import { ChargeLines } from "./ChargeLines";
import type { LedgerCharge } from "../../api";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

// jsPDF's built-in fonts are WinAnsi-only — no ₹ glyph and no non-Latin
// script support at all, so the exported PDF (unlike the on-screen view,
// which a browser renders fine) can't safely carry the ₹ symbol or any
// non-English label. "Rs." is the standard fallback for the former; the
// PDF's labels are intentionally kept in fixed English for the latter (see
// buildReceiptPdf below) rather than risk unshaped/garbled Indic script.
function fmtINR_ASCII(n: number) {
  return "Rs. " + Number(n).toLocaleString("en-IN");
}

function monthLabel(month: string) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export interface ReceiptEntry {
  id: string;
  month: string;
  amount: number;
  rentAmount?: number;
  charges?: LedgerCharge[];
  mode: string | null;
  paymentReference?: string | null;
  paidOn: string | null;
}

// Draws the receipt with jsPDF's native text/vector APIs (not a screenshot
// of the HTML) — keeps the file small and the text real/selectable/crisp at
// any zoom, matching what an actual receipt document should be. Labels are
// fixed English (see fmtINR_ASCII above for why) — only the user-entered
// data (names, property, payment mode) varies, and that's already Latin
// script in practice.
function buildReceiptPdf(props: {
  entry: ReceiptEntry;
  tenantName: string;
  propertyLabel: string;
  propertyLocality?: string | null;
  ownerName?: string | null;
}): jsPDF {
  const { entry, tenantName, propertyLabel, propertyLocality, ownerName } = props;
  const receiptNo = `RCT-${entry.id.slice(-8).toUpperCase()}`;

  // Rent-plus-extras months carry one extra row per line item (and a rent
  // row), so the page grows with them instead of clipping the bottom.
  const chargeRowCount = entry.charges?.length ? entry.charges.length + 1 : 0;
  const pageWidth = 380;
  const pageHeight = 560 + chargeRowCount * 31;
  const marginX = 32;
  const doc = new jsPDF({ unit: "pt", format: [pageWidth, pageHeight] });

  const brand: [number, number, number] = [75, 46, 131]; // #4B2E83
  const stampBg: [number, number, number] = [227, 245, 232]; // #E3F5E8
  const stampFg: [number, number, number] = [30, 138, 76]; // #1E8A4C
  const muted: [number, number, number] = [138, 130, 150]; // #8a8296
  const ink: [number, number, number] = [26, 20, 37]; // #1a1425
  const faint: [number, number, number] = [168, 159, 194]; // #a89fc2

  let y = 44;

  // Header: brand name + status stamp
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...brand);
  doc.text("ToLetBook", marginX, y);

  const stampText = "PAID";
  doc.setFontSize(9.5);
  const stampTextWidth = doc.getTextWidth(stampText);
  const stampBoxWidth = stampTextWidth + 22;
  const stampBoxHeight = 20;
  doc.setFillColor(...stampBg);
  doc.roundedRect(pageWidth - marginX - stampBoxWidth, y - 15, stampBoxWidth, stampBoxHeight, 9, 9, "F");
  doc.setTextColor(...stampFg);
  doc.text(stampText, pageWidth - marginX - stampBoxWidth / 2, y - 1, { align: "center" });

  y += 14;
  doc.setDrawColor(...brand);
  doc.setLineWidth(1.4);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 38;

  // Amount, centered
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  doc.text("AMOUNT PAID", pageWidth / 2, y, { align: "center" });
  y += 30;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...brand);
  doc.text(fmtINR_ASCII(entry.amount), pageWidth / 2, y, { align: "center" });
  y += 34;

  // Detail rows, each with a dashed divider below
  const rows: [string, string][] = [
    ["Receipt No", receiptNo],
    ["Paid by", tenantName],
    ["Property", propertyLabel + (propertyLocality ? `, ${propertyLocality}` : "")],
    ...(ownerName ? ([["Received by", ownerName]] as [string, string][]) : []),
    ["For the month of", monthLabel(entry.month)],
    ...(entry.charges?.length
      ? ([
          ["Rent", fmtINR_ASCII(entry.rentAmount ?? entry.amount - entry.charges.reduce((s, c) => s + c.amount, 0))],
          ...entry.charges.map((c): [string, string] => [
            c.kind === "electricity" ? "Electricity" : c.kind === "late_fee" ? "Late fee" : "Other charge",
            fmtINR_ASCII(c.amount),
          ]),
        ] as [string, string][])
      : []),
    ["Payment mode", (entry.mode ?? "—").toUpperCase()],
    ...(entry.paymentReference ? ([["Reference", entry.paymentReference]] as [string, string][]) : []),
    ["Paid on", fmtDate(entry.paidOn)],
  ];

  const labelWidth = 110;
  const valueMaxWidth = pageWidth - marginX * 2 - labelWidth;

  for (const [label, value] of rows) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...muted);
    doc.text(label, marginX, y);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ink);
    const valueLines = doc.splitTextToSize(value, valueMaxWidth);
    doc.text(valueLines, pageWidth - marginX, y, { align: "right" });

    const rowHeight = 15 * valueLines.length;
    y += rowHeight + 8;
    doc.setDrawColor(238, 238, 238);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(marginX, y - 8, pageWidth - marginX, y - 8);
    doc.setLineDashPattern([], 0);
    y += 8;
  }

  // Footer disclaimer
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...faint);
  const footerLines = doc.splitTextToSize(
    "This is a system-generated receipt from ToLetBook. Rent shown here is recorded within the app and not processed through a real payment gateway.",
    pageWidth - marginX * 2
  );
  doc.text(footerLines, pageWidth / 2, y, { align: "center" });

  return doc;
}

export function RentReceipt({
  entry,
  tenantName,
  propertyLabel,
  propertyLocality,
  ownerName,
  justPaid,
  onBack,
}: {
  entry: ReceiptEntry;
  tenantName: string;
  propertyLabel: string;
  propertyLocality?: string | null;
  ownerName?: string | null;
  justPaid?: boolean;
  onBack: () => void;
}) {
  const { t } = useLanguage();
  const receiptNo = `RCT-${entry.id.slice(-8).toUpperCase()}`;
  const filename = `receipt-${entry.month}-${receiptNo}.pdf`;
  const buildPdf = () => buildReceiptPdf({ entry, tenantName, propertyLabel, propertyLocality, ownerName });

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("rentReceipt.back")}
      </button>
      {justPaid && (
        <div
          style={{
            background: "var(--success-tint)",
            color: "var(--success)",
            fontWeight: 700,
            fontSize: 12.5,
            padding: "10px 14px",
            borderRadius: 12,
            marginBottom: 14,
          }}
        >
          {t("rentReceipt.paymentRecordedBanner")}
        </div>
      )}
      <div className="reg-title">{t("rentReceipt.title")}</div>
      <div className="reg-sub">{monthLabel(entry.month)}</div>

      <div className="card" style={{ marginTop: 4 }}>
        <div className="card-row">
          <span className="sub">{t("rentReceipt.amountPaid")}</span>
          <span className="badge paid">{fmtINR(entry.amount)}</span>
        </div>
        <ChargeLines rentAmount={entry.rentAmount} charges={entry.charges} total={entry.amount} />
      </div>

      <div className="card">
        <div className="card-row">
          <span className="sub">{t("rentReceipt.receiptNoLabel")}</span>
          <b>{receiptNo}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentReceipt.paidByLabel")}</span>
          <b>{tenantName}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentReceipt.propertyLabel")}</span>
          <b>
            {propertyLabel}
            {propertyLocality ? `, ${propertyLocality}` : ""}
          </b>
        </div>
        {ownerName && (
          <div className="card-row" style={{ marginTop: 8 }}>
            <span className="sub">{t("rentReceipt.receivedByLabel")}</span>
            <b>{ownerName}</b>
          </div>
        )}
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentReceipt.paymentModeLabel")}</span>
          <b>{(entry.mode ?? "—").toUpperCase()}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("rentReceipt.paidOnLabel")}</span>
          <b>{fmtDate(entry.paidOn)}</b>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-outline" style={{ flex: 1 }} onClick={() => downloadBlob(filename, buildPdf().output("blob"))}>
          {t("rentReceipt.downloadBtn")}
        </button>
        <button
          className="btn-solid"
          style={{ flex: 1 }}
          onClick={() =>
            shareOrDownloadBlob(
              filename,
              buildPdf().output("blob"),
              "application/pdf",
              t("rentReceipt.shareTitle", { month: monthLabel(entry.month) }),
              t("rentReceipt.shareText", { receiptNo, amount: fmtINR(entry.amount) })
            )
          }
        >
          {t("rentReceipt.shareBtn")}
        </button>
      </div>
    </>
  );
}
